import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { LocationOn, People, CurrencyRupee, Star, Navigation, LocalOffer, EmojiEvents, Today } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import GradientSurface from './shared/GradientSurface';
import { useReducedMotionPreferred } from '../hooks/useReducedMotionPreferred';

function LibraryCard({ library, compact = false }) {
  const navigate = useNavigate();
  const location = useLocation();
  const reducedMotion = useReducedMotionPreferred();

  const placeholderImages = [
    'https://images.unsplash.com/photo-1521587760476-6c12a4b040da?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1507842217343-583bb7270b66?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1481627834876-b7833e8f5570?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1519682337058-a94d519337bc?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1524995997946-a1c2e315a42f?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1577985051167-0d49eec21977?w=800&h=600&fit=crop',
    'https://images.unsplash.com/photo-1526243741027-444d633d7365?w=800&h=600&fit=crop',
  ];

  const getImageUrl = () => {
    if (library.gallery && library.gallery.length > 0) {
      const imagePath = library.gallery[0];
      if (imagePath.startsWith('/uploads/')) {
        const baseUrl = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';
        return `${baseUrl}${imagePath}`;
      }
      return imagePath;
    }
    const hash = library.id.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return placeholderImages[hash % placeholderImages.length];
  };

  const hoverProps = reducedMotion
    ? {}
    : { whileHover: { y: -4, boxShadow: '0 16px 40px rgba(0,0,0,0.16)' } };

  return (
    <motion.div
      {...hoverProps}
      transition={{ type: 'spring', stiffness: 320, damping: 28 }}
      style={{ height: '100%' }}
    >
      <GradientSurface
        variant="surface"
        onClick={() => {
          const inStudentShell = location.pathname.startsWith('/student');
          navigate(inStudentShell ? `/student/libraries/${library.id}` : `/libraries/${library.id}`);
        }}
        sx={{
          cursor: 'pointer',
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 'shadowsExt.sm',
          ...(compact && {
            borderTopLeftRadius: '6px',
            borderTopRightRadius: '6px',
          }),
        }}
      >
        {/* Cover image — 16/10 aspect ratio */}
        <Box
          sx={{
            position: 'relative',
            aspectRatio: '16/10',
            overflow: 'hidden',
            bgcolor: 'background.subtle',
          }}
        >
          <Box
            component="img"
            src={getImageUrl()}
            alt={library.name}
            loading="lazy"
            decoding="async"
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              display: 'block',
            }}
          />

          {/* Top-right badges */}
          <Box
            sx={{
              position: 'absolute',
              top: 10,
              right: 10,
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              alignItems: 'flex-end',
            }}
          >
            {library.top_rated && (
              <Chip
                icon={<EmojiEvents sx={{ fontSize: 14, color: 'text.primary !important' }} />}
                label="Top Rated"
                size="small"
                sx={{
                  bgcolor: 'warning.main',
                  color: 'text.primary',
                  fontWeight: 700,
                  backdropFilter: 'blur(10px)',
                }}
              />
            )}
            {library.hasOneDayPass && (
              <Chip
                icon={<Today sx={{ fontSize: 14, color: 'common.white !important' }} />}
                label={`₹${library.dailyPlanPrice}/day`}
                size="small"
                sx={{
                  bgcolor: 'success.main',
                  color: 'common.white',
                  fontWeight: 700,
                  backdropFilter: 'blur(10px)',
                }}
              />
            )}
            {library.active && !library.top_rated && !library.hasOneDayPass && (
              <Chip
                label="Open"
                size="small"
                sx={{
                  bgcolor: 'success.main',
                  color: 'common.white',
                  fontWeight: 700,
                  backdropFilter: 'blur(10px)',
                }}
              />
            )}
          </Box>

          {/* Distance badge */}
          {library.distance !== null && library.distance !== undefined && (
            <Chip
              icon={<Navigation sx={{ fontSize: 14, color: 'common.white !important' }} />}
              label={`${library.distance.toFixed(1)} km`}
              size="small"
              sx={{
                position: 'absolute',
                top: 10,
                left: 10,
                bgcolor: 'primary.main',
                color: 'common.white',
                fontWeight: 700,
                backdropFilter: 'blur(10px)',
              }}
            />
          )}

          {/* Rating overlay */}
          {(library.average_overall_rating || library.total_ratings > 0) && (
            <Box
              sx={{
                position: 'absolute',
                bottom: 10,
                left: 10,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                bgcolor: 'rgba(255,255,255,0.95)',
                backdropFilter: 'blur(10px)',
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
              }}
            >
              <Star sx={{ fontSize: 16, color: 'warning.main' }} />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                {parseFloat(library.average_overall_rating || 0).toFixed(1)}
              </Typography>
              {library.total_ratings > 0 && (
                <Typography variant="caption" color="text.secondary">
                  ({library.total_ratings})
                </Typography>
              )}
            </Box>
          )}

          {/* Discount badge */}
          {library.maxFirstTimeDiscount && (
            <Chip
              icon={<LocalOffer sx={{ fontSize: 12, color: 'common.white !important' }} />}
              label={`${library.maxFirstTimeDiscount}% off for new users`}
              size="small"
              sx={{
                position: 'absolute',
                bottom: 10,
                right: 10,
                bgcolor: 'primary.dark',
                color: 'common.white',
                fontWeight: 600,
                fontSize: '0.65rem',
                backdropFilter: 'blur(10px)',
              }}
            />
          )}
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, p: compact ? 1.5 : 2.5 }}>
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              mb: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {library.name}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, mb: 2 }}>
            <LocationOn sx={{ fontSize: 16, color: 'text.secondary', mt: 0.25, flexShrink: 0 }} />
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                lineHeight: 1.5,
              }}
            >
              {library.city ? `${library.city} • ` : ''}{library.address}
            </Typography>
          </Box>

          {/* Seats + Price — hidden in compact mode */}
          {!compact && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <People sx={{ fontSize: 16, color: 'primary.main' }} />
                <Typography variant="body2" fontWeight={600}>
                  {library.capacity} seats
                </Typography>
              </Box>

              {library.minPrice !== null && library.minPrice !== undefined && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CurrencyRupee sx={{ fontSize: 16, color: 'primary.main' }} />
                  {library.maxFirstTimeDiscount && library.minDiscountedPrice < library.minPrice ? (
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={600} color="primary.main">
                        From ₹{library.minDiscountedPrice}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ textDecoration: 'line-through' }}
                      >
                        ₹{library.minPrice}
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="body2" fontWeight={600} color="primary.main">
                      From ₹{library.minPrice}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          )}

          {/* Amenity chips using primary.soft — hidden in compact mode */}
          {!compact && library.amenities?.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1.5 }}>
              {library.amenities.slice(0, 3).map((amenity) => (
                <Chip
                  key={amenity}
                  label={amenity}
                  size="small"
                  sx={{
                    fontSize: '0.68rem',
                    height: 22,
                    bgcolor: 'primary.soft',
                    color: 'primary.dark',
                    fontWeight: 500,
                    border: 'none',
                  }}
                />
              ))}
              {library.amenities.length > 3 && (
                <Chip
                  label={`+${library.amenities.length - 3}`}
                  size="small"
                  sx={{
                    fontSize: '0.68rem',
                    height: 22,
                    bgcolor: 'background.subtle',
                    color: 'text.secondary',
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </GradientSurface>
    </motion.div>
  );
}

export default LibraryCard;
